import PropTypes from 'prop-types';
import { Component } from 'react';
import lodash from 'lodash';

import DateTime from 'common/components/DateTime/DateTime';
import StandardRichTextEditor from 'common/components/RichTextEditor/StandardRichTextEditor';
import FilePreview from 'common/components/FilePreview/FilePreview';
import { UserHasEmailRemindersFeatureAccess } from 'common/features';
import {
    Container,
    FormSection,
    parseAndEscapeDescription,
} from 'forms/components/common';
import { DataGrid } from 'common/components/DataGrid/DataGrid';
import { trackEvent } from 'components/common/GoogleAnalytics/GoogleAnalytics';

function isTaskFinished(task) {
    return lodash.get(task, 'finished', false);
}

function isTaskCanceled(task) {
    return lodash.get(task, 'deleted', false);
}

function isTaskCancelable(task) {
    return lodash.get(task, 'id', false) && !isTaskFinished(task) && !isTaskCanceled(task);
}

function getUserStatusText(isFinished, isCanceled) {
    if (isFinished) {
        return 'Complete';
    }
    if (isCanceled) {
        return 'Canceled';
    }
    return 'Incomplete';
}

function trackEventGoogleAnalytics() {
    const category = 'workflow';
    const action = 'Cancel Task';
    trackEvent({category, action});
}

class SdfaSentItem extends Component {

    get recipients() {
        const { recipients, tasks } = this.props.sentItem;
        const tasksByAssignee = lodash.keyBy(tasks, 'assignee');
        return lodash.map(recipients, ({ sub, emailAddress, displayName }) => {
            const task = tasksByAssignee[sub];
            return {
                sub,
                emailAddress,
                displayName,
                taskId: lodash.get(task, 'id', null),
                isCompleted: isTaskFinished(task),
                isCanceled: isTaskCanceled(task),
                isCancelable: isTaskCancelable(task),
            };
        });
    }

    render() {
        const {
            title,
            dueDate,
            reminderDate,
            description,
            status,
            actionName,
            document
        } = this.props.sentItem;

        const { viewDocumentPermission } = this.props;
        const { recipients } = this;
        const ReminderDateField = UserHasEmailRemindersFeatureAccess(ReminderDateFormSection);
        const { descriptionData, hasDescription } = parseAndEscapeDescription(description);

        return (
            <Container data-cy="SdfaSentItem">
                <FormSection headerText="Title" data-cy="SdfaSentItem-Title">
                    {title}
                </FormSection>

                <FormSection headerText="Due Date" data-cy="SdfaSentItem-Due">
                    <DateTime value={dueDate} time={false}/>
                </FormSection>

                <ReminderDateField reminderDate={reminderDate} format={'DD MMM YYYY'}/>

                <FormSection headerText="Status" data-cy="SdfaSentItem-Status">
                    {lodash.startCase(status)}
                </FormSection>

                <FormSection headerText="Requested Action" data-cy="SdfaSentItem-Requested">
                    {lodash.startCase(actionName)}
                </FormSection>

                { hasDescription && (
                    <FormSection headerText="Description" data-cy="SdfaSentItem-Description">
                        <StandardRichTextEditor
                            readOnly={true}
                            content={descriptionData} />
                    </FormSection>
                ) }

                <FormSection headerText="Document Requesting Action" data-cy="SdfaSentItem-Document">
                    <FilePreview
                        document={document}
                        onClick={this.props.viewDocument}
                        viewDocumentPermission = {viewDocumentPermission} />
                </FormSection>

                <FormSection headerText="User Assignments" data-cy="SdfaSentItem-User">
                    <UsersList
                        recipients={recipients}
                        cancelTask={this.props.cancelTask}/>
                </FormSection>
            </Container>
        );
    }
}

const ReminderDateFormSection = ({ reminderDate }) => (
    <FormSection headerText="Reminder Date" data-cy="SdfaSentItem-Reminder">
        <DateTime value={reminderDate} time={false} />
    </FormSection>
);

const CancelButton = ({ rawData, cancelTask, trackEvent }) => {
    const { taskId, isCancelable } = rawData;
    const visibility = isCancelable ? 'visible' : 'hidden';
    const onClick = () => {
        trackEvent();
        cancelTask(taskId);
    };
    return <button type='button' style={{visibility}} onClick={onClick} data-cy="SdfaSentItem-Cancel">Cancel</button>;
};

const UsersList = ({ recipients, cancelTask }) => {
    const userListGridFields = [{
        fieldKey: 'displayName',
        valueSelector: 'displayName',
        weight: 3,
        displayOrder: 1,
        header: 'Name'
    }, {
        fieldKey: 'emailAddress',
        valueSelector: 'emailAddress',
        weight: 3,
        displayOrder: 2,
        header: 'Email'
    }, {
        fieldKey: 'status',
        valueSelector: recipient => getUserStatusText(recipient.isCompleted, recipient.isCanceled),
        weight: 1,
        displayOrder: 3,
        header: 'Status'
    }, {
        fieldKey: 'cancelButton',
        valueSelector: recipient => null,
        contentTemplate: <CancelButton cancelTask={cancelTask} trackEvent={trackEventGoogleAnalytics} />,
        weight: 1,
        displayOrder: 4,
        header: 'Cancel',
        contentRowAlign: 'center',
        contentColumnAlign: 'center',
        headerRowAlign: 'center',
    }];

    return (
        <DataGrid
            data={recipients}
            fields={userListGridFields}
            showHeaders 
            data-cy="SdfaSentItem-DataGrid"/>
    );
};

SdfaSentItem.propTypes = {
    sentItem: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        dueDate: PropTypes.string.isRequired,
        reminderDate: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
        actionName: PropTypes.string.isRequired,
        recipients: PropTypes.arrayOf(PropTypes.shape({
            sub: PropTypes.string,
            emailAddress: PropTypes.string,
            displayName: PropTypes.string,
        })),
        tasks: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string,
            assignee: PropTypes.string,
            finished: PropTypes.bool,
            deleted: PropTypes.bool,
        })),
        document: PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            uploadUser: PropTypes.shape({
                displayName: PropTypes.string.isRequired,
                emailAddress: PropTypes.string.isRequired
            }),
            uploadDate: PropTypes.string.isRequired
        }),
    }).isRequired,

};

export default SdfaSentItem;
